import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { IAnswer, IApplicationFormIndex, IHttpResponse } from '@boldpenguin/sdk-types';
import {
  BqxApplicationAbstractService,
  IApplicationCloneParam,
  IApplicationSearchFactory,
  IApplicationSearchParam,
  ISearchableAnswer,
  ISearchableQuestionParam
} from 'bqx-application';
import { LogLevel, LoggerAbstractService } from 'bqx-logging';
import { NGXLogger } from 'ngx-logger';
import { Observable, ReplaySubject, Subject, from } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AppConfig } from 'src/app/app.config';
import { ISearchInputs } from 'src/app/models/application-form/search-form.model';
import { FormGroupStringType } from 'src/app/models/shared/shared-types';
import { ApplicationSearchAbstractService } from './application-search.abstract.service';

@Injectable()
export class ApplicationSearchService implements ApplicationSearchAbstractService {
  private _searchFormUnsub = new Subject<void>();
  private _objBpSearch: IApplicationSearchFactory<
    IHttpResponse<[IApplicationFormIndex]>,
    IApplicationSearchParam,
    IApplicationCloneParam,
    ISearchableQuestionParam,
    ISearchableAnswer[]
  >;
  private _searchForm: IHttpResponse<[IApplicationFormIndex]>;
  private _searchFormSub$ = new ReplaySubject<IHttpResponse<[IApplicationFormIndex]>>();
  private _areSearchLoading$ = new ReplaySubject<boolean>();
  private _isSearchFormReady$ = new ReplaySubject<boolean>();
  private _searchFilters: FormGroup<FormGroupStringType>;
  private _filterOption: ISearchInputs[];
  private carrierChoices = [
    '--',
    'MSA',
    'Insureon',
    'Progressive',
    'Infinity',
    'Hiscox',
    'Nationwide',
    'CNA',
    'Evanston Off-platform',
    'Homesite',
    'Amtrust',
    'Liberty Mutual',
    'Evanston',
    'Markel',
    'Arch Insurance'
  ];

  public get isSearchFormReady$(): Observable<boolean> {
    return this._isSearchFormReady$.asObservable();
  }

  public set isSearchFormReady$(status: boolean) {
    this._isSearchFormReady$.next(status);
  }

  public get areSearchLoading$(): Observable<boolean> {
    return this._areSearchLoading$.asObservable();
  }

  public get searchFilters(): FormGroup<FormGroupStringType> {
    return this._searchFilters;
  }

  public get filterOption(): ISearchInputs[] {
    return this._filterOption;
  }

  public set sort(sort: Sort) {
    this._searchParams.sortColumn = sort.active;
    this._searchParams.sortOrder = sort.direction as 'asc' | 'desc';
  }

  set page(page: PageEvent) {
    this._searchParams.pageNumber = page.pageIndex + 1;
    this._searchParams.pageSize = page.pageSize;
  }

  constructor(
    private bqxApplication: BqxApplicationAbstractService,
    private http: HttpClient,
    private loggerService: LoggerAbstractService,
    private logger: NGXLogger
  ) {
    this.getFilterQuestion();
  }

  public get searchResult$(): Observable<IHttpResponse<[IApplicationFormIndex]>> {
    return this._searchFormSub$.asObservable();
  }

  private _searchParams: IApplicationSearchParam = {
    sortColumn: AppConfig.settings.applicationSearch.defaultSortColumn,
    sortOrder: AppConfig.settings.applicationSearch.defaultSortOrder as 'asc' | 'desc',
    pageNumber: AppConfig.settings.applicationSearch.defaultPage,
    pageSize: AppConfig.settings.applicationSearch.defaultPageSize,
    answers: []
  };

  public getApplicationForms(): void {
    this._areSearchLoading$.next(false);
    const option = {
      applicationType: 'Agent'
    };
    this._objBpSearch = this.bqxApplication.getSearchForm(option);
    from(this._objBpSearch.searchApplicationForms(this._searchParams))
      .pipe(takeUntil(this._searchFormUnsub))
      .subscribe((result) => {
        this._searchForm = result;
        this._areSearchLoading$.next(true);
        this._searchFormSub$.next(this._searchForm);
        this._searchFormUnsub.next();
        this._searchFormUnsub.complete();
      });
  }

  private getFilterQuestion() {
    this._isSearchFormReady$.next(false);
    const option = {
      applicationType: 'Agent'
    };
    this._objBpSearch = this.bqxApplication.getSearchForm(option);
    const param: ISearchableQuestionParam = {
      order: 'asc',
      page: 1,
      per_page: 10
    };
    this._objBpSearch.getSearchOptions(this.http, param).subscribe((res) => {
      const options = res.body;
      this._filterOption = [];
      options.forEach((option) => {
        switch (option.field_type) {
          case 'text':
            this._filterOption.push({
              id: option.id,
              code: option.code,
              order: option.order,
              field_type: 'text',
              displayName: option.text
            });
            break;
          case 'tenant':
            if (option.text === 'Last Viewed') option.text = 'Consumer Last Viewed';
            this._filterOption.push({
              id: option.id,
              code: option.code,
              order: option.order,
              field_type: 'dropdown',
              displayName: option.text,
              choices: this.carrierChoices
            });
            break;
          default:
            break;
        }
      });
      this.createSearchFilterOption();
      this._isSearchFormReady$.next(true);
    });
  }

  private createSearchFilterOption(): void {
    const formControlsGroup: FormGroupStringType = {};
    this._searchFilters = new FormGroup<FormGroupStringType>(formControlsGroup);
    this._filterOption.forEach((option) => {
      const inputControl = new FormControl<string>(null);
      this._searchFilters.addControl(option.code, inputControl);
      inputControl.valueChanges.pipe(debounceTime(300)).subscribe(
        (value: string) => {
          value =
            value === '--' && option.field_type === 'dropdown'
              ? undefined
              : option.field_type === 'dropdown' && value === 'Arch Insurance'
              ? 'arch'
              : value;
          const ans = {
            question: {
              id: option.id
            },
            value: value
          };
          this._searchParams.answers.push(ans as IAnswer);
          this.getApplicationForms();
        },
        (err: string) => {
          this.loggerService.postLogData(LogLevel.Error, 'application-search.service: createSearchFilterOption', err, '')
        }
      );
    });
  }
}
